import React, { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  verifyUserOtp,
  userLogin,
  resendOtp,
  checkUserOtp,
} from "../../utils/authService";
import { joinLiveClass, getLiveClassDataById } from "../../utils/liveClasses";
import { useNavigate } from "react-router-dom";
import { setToken } from "../../utils/tokenStorage"; // Ensure you have a utility function to store the token
import { useDispatch } from "react-redux";
import LazyLoad from "react-lazyload";
import {
  setUserInformation,
  setUserPermissions,
  setUserRole,
} from "../../store/userRole";
import { useAppContext } from "../../AppContext";
import { fetchCategories } from "../../store/categoriesSlice";
import { clearSubscription } from "../../store/subscriptionSlice";
import { openLinkInNewTab } from "../../pages/StudentPages/LiveClasses/utils";
import { consumeStudentRedirectUrl } from "../../utils/redirectStorage";
import { getDeviceId } from "./LoginForm";

const OtpComponent = ({
  email,
  password, // Receive the password from parent
  liveclassid,
  setShowOtp,
  purpose = "registration",
  onSuccess,
  onOtpSuccess,
  loginMethod,
  redirectUrl, // Receive the redirect URL from parent
}) => {
  const [otp, setOtp] = useState({
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
  });
  const [otpReady, setOtpReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60); // 1-minute countdown
  const [resendEnabled, setResendEnabled] = useState(false); // Resend button control
  const navigate = useNavigate();
  const storeDispatch = useDispatch();
  const { dispatch } = useAppContext();

  const inputRefs = {
    otp1: useRef(null),
    otp2: useRef(null),
    otp3: useRef(null),
    otp4: useRef(null),
  };

  // Countdown logic
  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setResendEnabled(true); // Enable resend button when timer hits 0
    }
  }, [timer]);

  const handleOtpChange = (field, value) => {
    setOtp((prevOtp) => {
      const newOtp = { ...prevOtp, [field]: value };
      if (Object.values(newOtp).every((digit) => digit.length === 1)) {
        setOtpReady(true);
      } else {
        setOtpReady(false);
      }
      return newOtp;
    });

    if (value.length === 1 && field !== "otp4") {
      const nextField = `otp${parseInt(field.replace("otp", "")) + 1}`;
      inputRefs[nextField].current.focus();
    }
  };

  const verifyOtp = async () => {
    if (purpose === "passwordReset") {
      const enteredOtp = `${otp.otp1}${otp.otp2}${otp.otp3}${otp.otp4}`;
      const response = await checkUserOtp(
        JSON.stringify({ email, otp: enteredOtp }),
      );
      if (response.ok) {
        toast.success("OTP verified successfully!");
        onSuccess(enteredOtp);
      } else {
        toast.error("OTP verification failed. Please try again.");
      }
    } else {
      const enteredOtp = `${otp.otp1}${otp.otp2}${otp.otp3}${otp.otp4}`;
      setLoading(true);
      try {
        const response = await verifyUserOtp(
          JSON.stringify({
            [loginMethod === "phone" ? "phone" : "email"]: email,
            otp: enteredOtp,
          }),
        );

        if (response.data.token && response.data.user) {
          const { user, token } = response.data;

          if (user && token) {
            toast.success("OTP verified successfully!");
            setToken(token);
            const roleOfUser = user.roleName;
            storeDispatch(setUserRole(roleOfUser));
            storeDispatch(setUserInformation(user));
            storeDispatch(setUserPermissions(user.permissions));
            dispatch({ type: "SET_AUTH_STATUS", payload: true });
            dispatch(fetchCategories());
            sessionStorage.setItem("login-in-progress", "true");
            // Call onOtpSuccess/onSuccess callback with user role if provided
            if (onOtpSuccess) {
              onOtpSuccess(roleOfUser);
              setShowOtp(false);
              return;
            }
            if (onSuccess) {
              onSuccess(roleOfUser);
              setShowOtp(false);
              return;
            }

            // Role-based redirect after successful OTP verification
            const adminRoles = [
              "Admin",
              "SuperAdmin",
              "Root",
              "Teacher",
              "Watcher",
            ];
            const isAdminUser =
              adminRoles.includes(roleOfUser) || roleOfUser !== "Student";

            if (isAdminUser) {
              // For non-students, clear any stored student redirect URL and use default route
              consumeStudentRedirectUrl(); // Clear the stored URL
              navigate("/myClassrooms", { replace: true });
            } else {
              // For students, check for stored redirect URL first, then URL params, then default
              const storedRedirectUrl = consumeStudentRedirectUrl();
              if (storedRedirectUrl) {
                navigate(storedRedirectUrl, { replace: true });
              } else if (redirectUrl !== null) {
                const fullRedirectUrl = `/${redirectUrl}`;
                navigate(fullRedirectUrl, { replace: true });
              } else {
                navigate("/student/myClassrooms", { replace: true });
              }
            }

            // Clear subscription data after setting user information
            storeDispatch(clearSubscription());
          } else {
            toast.error("Login failed. Please try again.");
          }
        } else if (response.ok) {
          toast.success("OTP verified successfully!");

          if (liveclassid) {
            // Log the user in after OTP verification if liveclassid is provided
            const deviceId = await getDeviceId();

            const loginResponse = await userLogin({
              email,
              password,
              deviceId,
            });
            if (loginResponse.ok && loginResponse.data.token) {
              localStorage.setItem("dmapid", deviceId);
              setToken(loginResponse.data.token);
              const roleOfUser = loginResponse.data.user.roleName;
              storeDispatch(setUserRole(roleOfUser));
              storeDispatch(setUserInformation(loginResponse.data.user));
              storeDispatch(
                setUserPermissions(loginResponse.data.user.permissions),
              );
              dispatch({ type: "SET_AUTH_STATUS", payload: true });
              dispatch(fetchCategories());
              sessionStorage.setItem("login-in-progress", "true");

              // Clear subscription data after setting user information
              storeDispatch(clearSubscription());

              // Fetch live class details and join the class
              const liveClassResponse = await getLiveClassDataById(liveclassid);
              if (liveClassResponse.ok) {
                const { meetingID, attendeePW } =
                  liveClassResponse.data.liveClass;
                const joinResponse = await joinLiveClass(
                  liveclassid,
                  JSON.stringify({ meetingID, password: attendeePW }),
                  "join",
                );
                if (joinResponse.ok && joinResponse.data.url) {
                  openLinkInNewTab(joinResponse.data.url);
                  // Role-based redirect after joining live class
                  const adminRoles = [
                    "Admin",
                    "SuperAdmin",
                    "Root",
                    "Teacher",
                    "Watcher",
                  ];
                  const isAdminUser =
                    adminRoles.includes(roleOfUser) || roleOfUser !== "Student";

                  if (isAdminUser) {
                    navigate("/myClassrooms", { replace: true });
                  } else {
                    navigate("/student/myClassrooms", { replace: true });
                  }
                } else {
                  toast.error("Failed to join live class.");
                }
              } else {
                toast.error("Failed to fetch live class details.");
              }
            } else {
              toast.error("Login failed. Please try again.");
            }
          } else {
            const deviceId = await getDeviceId();
            const loginResponse = await userLogin({
              [loginMethod === "phone" ? "phone" : "email"]: email,
              password,
              deviceId,
            });
            if (loginResponse.ok && loginResponse.data.token) {
              localStorage.setItem("dmapid", deviceId);
              setToken(loginResponse.data.token);
              const roleOfUser = loginResponse.data.user.roleName;
              storeDispatch(setUserRole(roleOfUser));
              storeDispatch(setUserInformation(loginResponse.data.user));
              storeDispatch(
                setUserPermissions(loginResponse.data.user.permissions),
              );
              dispatch({ type: "SET_AUTH_STATUS", payload: true });
              dispatch(fetchCategories());

              // Clear subscription data after setting user information
              storeDispatch(clearSubscription());

              // Call onOtpSuccess to handle success action (e.g., redirection)
              if (onOtpSuccess) {
                onOtpSuccess(roleOfUser);
              } else {
                // If no callback provided, handle redirect here
                const adminRoles = [
                  "Admin",
                  "SuperAdmin",
                  "Root",
                  "Teacher",
                  "Watcher",
                ];
                const isAdminUser =
                  adminRoles.includes(roleOfUser) || roleOfUser !== "Student";

                if (isAdminUser) {
                  // For non-students, clear any stored student redirect URL and use default route
                  consumeStudentRedirectUrl(); // Clear the stored URL
                  navigate("/myClassrooms", { replace: true });
                } else {
                  // For students, check for stored redirect URL first, then URL params, then default
                  const storedRedirectUrl = consumeStudentRedirectUrl();
                  if (storedRedirectUrl) {
                    navigate(storedRedirectUrl, { replace: true });
                  } else if (redirectUrl !== null) {
                    const fullRedirectUrl = `/${redirectUrl}`;
                    navigate(fullRedirectUrl, { replace: true });
                  } else {
                    navigate("/student/myClassrooms", { replace: true });
                  }
                }
              }
              setShowOtp(false);
            } else {
              toast.error("Login failed. Please try again.");
            }
          }
        } else {
          toast.error("OTP verification failed. Please try again.");
        }
      } catch (error) {
        console.error("OTP verification error:", error);
        toast.error("OTP verification failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await resendOtp({ email });
      if (response.ok) {
        toast.success("OTP resent successfully!");
        setTimer(60); // Reset timer for 1 minute
        setResendEnabled(false); // Disable resend button
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="otp-main-div">
        <div className="otp-icon-div">
          <LazyLoad height={20} offset={100}>
            <img src="/Flat.svg" alt="otp-icon" />
          </LazyLoad>
        </div>
        <div className="d-md-flex d-block text-md-start text-center align-items-center justify-content-md-start justify-content-center mb-3">
          <label className="otp-heading">
            Enter the verification code that we sent to you
          </label>
        </div>
        <div className="d-flex justify-content-between">
          <input
            ref={inputRefs.otp1}
            style={otpInputStyle}
            type="text"
            maxLength="1"
            name="otp1"
            value={otp.otp1}
            onChange={(e) => handleOtpChange("otp1", e.target.value)}
          />
          <input
            ref={inputRefs.otp2}
            style={otpInputStyle}
            type="text"
            maxLength="1"
            name="otp2"
            value={otp.otp2}
            onChange={(e) => handleOtpChange("otp2", e.target.value)}
          />
          <input
            ref={inputRefs.otp3}
            style={otpInputStyle}
            type="text"
            maxLength="1"
            name="otp3"
            value={otp.otp3}
            onChange={(e) => handleOtpChange("otp3", e.target.value)}
          />
          <input
            ref={inputRefs.otp4}
            style={otpInputStyle}
            type="text"
            maxLength="1"
            name="otp4"
            value={otp.otp4}
            onChange={(e) => handleOtpChange("otp4", e.target.value)}
          />
        </div>
        <Button
          variant="primary"
          className="mt-3"
          onClick={verifyOtp}
          disabled={!otpReady || loading}
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </Button>

        {/* Timer display */}
        {timer > 0 ? (
          <p>OTP will expire in {timer} seconds</p>
        ) : (
          <Button
            variant="secondary"
            className="mt-3 mx-2"
            onClick={handleResendOtp}
            disabled={!resendEnabled || loading}
          >
            Resend OTP
          </Button>
        )}
      </div>
    </div>
  );
};

const otpInputStyle = {
  width: "70px",
  height: "70px",
  fontSize: "200%",
  borderRadius: "7.845px",
  border: "2.441px solid var(--Neutral-Danger-7, #ECECEC)",
  padding: "12px",
  textAlign: "center",
  marginRight: "10px",
};

export default OtpComponent;
